/**
  This module provides a caching solution to keep generated client reports in the browser's Local Storage.

  Reports can be saved and retrieved via simple functions.
  Also, the `bind()` function allows to bind a UI to change events such as expiration of cached reports.
  Therefore, `bind()` will call the provided callback function with the list of remaining reports in storage
  in a regular interval.
 */

define(['moment',
        'core/browser-storage'],
    function(moment, browser_storage) {

        var DEBUG = false,
            STORAGE_KEY = 'StoredReports',
            MINUTES_TO_CACHE_REPORT = 22 * 60,
            CLEANUP_INTERVAL = 5 * 60 * 1000

        function debug(message) {
            if (DEBUG) {
                console.log('report_storage: ' + message);
            }
        }

        function warn(message) {
            console.log('report_storage: ' + message);
        }

        function validate(report) {
            if (!report) {
                warn('Empty report cannot be saved');
                return false;
            }
            if (!report.tid) {
                warn('Report is missing TID');
                return false;
            }
            if (!report.time_period) {
                warn('Report is missing time period');
                return false;
            }
            if (!report.client_names) {
                warn('Report is missing client names');
                return false;
            }
            if (!report.expiresAt) {
                warn('Report is missing expiry date');
                return false;
            }
            return true;
        }

        function saveAllReports(reports) {
            browser_storage.setItem(STORAGE_KEY, JSON.stringify(reports));
        }

        function getAllReports() {
            var serializedReports = browser_storage.getItem(STORAGE_KEY) || '[]',
                reports = JSON.parse(serializedReports);

            function outputReports(reports) {
                result = '';
                for (var i=0; i<reports.length; i++) {
                    result += reports[i].tid + ',';
                }
                return result;
            }

            function sortByCreatedAtDesc(a, b) {
                var momentA = moment(a.createdAt),
                    momentB = moment(b.createdAt);

                if (momentA.isBefore(momentB)) {
                    return -1;
                }
                if (momentA.isAfter(momentB)) {
                    return 1;
                }
                return 0;
            }

            return reports.sort(sortByCreatedAtDesc);
        }

        function nextExpiryDate() {
            return moment().add(MINUTES_TO_CACHE_REPORT, 'minutes');
        }

        function copyTimestamps(old_report, new_report) {
            if (old_report) {
                if (old_report.expiresAt) {
                    new_report.expiresAt = old_report.expiresAt;
                }
                if (old_report.createdAt) {
                    new_report.createdAt = old_report.createdAt;
                }
            }
        }

        function saveReport(report) {
            var reports = getAllReports(),
                idx = getReportIdx(report.tid),
                old_report = undefined;

            debug('Saving report with TID "' + report.tid + '" to Local Storage');

            // TODO Use expiration date from backend to avoid problems when client went to sleep while generation was still ongoing...
            report.expiresAt = nextExpiryDate().format();
            report.createdAt = moment().format();

            if (idx > -1) {
                var removed_reports = reports.splice(idx, 1);
                old_report = removed_reports.length > 0 ? removed_reports[0] : undefined;
                copyTimestamps(old_report, report);
            }

            if (report.success === false) {
                debug('Not saving report with TID "' + report.tid + '" because it was unsuccessful');
            } else if (!validate(report)) {
                warn('Not saving report because of validation errors');
            } else {
                reports.push(report);
            }


            saveAllReports(reports);
        }

        function getReportIdx(tid) {
            var reports = getAllReports();
            for (var i=0; i < reports.length; i++) {
                var report = reports[i];
                if (report.tid === tid) {
                    return i;
                }
            }
            return -1;
        }

        function getReport(tid) {
            var idx = getReportIdx(tid);

            if (idx > -1) {
                return getAllReports()[idx];
            }
        }

        function resetReports() {
            return browser_storage.removeItem(STORAGE_KEY);
        }

        function cleanExpiredReports() {
            var reports = getAllReports(),
                lastValidMoment = moment(),
                initialNumberOfReports = reports.length;

            for (var i=0; i < reports.length; i++) {
                var report = reports[i];

                if (moment(report.expiresAt).isSameOrBefore(lastValidMoment)) {
                    reports.splice(i, 1);
                    debug('Removed expired report: ' + JSON.stringify(report));
                }
            }
            if (initialNumberOfReports != reports.length) {
                debug('There are ' + reports.length + ' reports left in Local Storage');
            }
            saveAllReports(reports);
        }

        function bind(onUpdate) {
            function cleanExpiredAndUpdateUi() {
                cleanExpiredReports();

                if (onUpdate) {
                    onUpdate(getAllReports());
                }
            }

            setInterval(cleanExpiredAndUpdateUi, CLEANUP_INTERVAL);
            cleanExpiredAndUpdateUi();
        }

        return {
            bind: bind,
            save: saveReport,
            get: getReport,
            reset: resetReports
        };
    }
);
